import { create } from 'zustand';

/**
 * Transport state for the ad flow (§8: WATCHING_AD, ACTIVATING, ERROR).
 *
 * These states belong to the client — they describe what the *user* is looking
 * at while the network works. The miner's real state still comes from the
 * server on the next read.
 *
 * `progress` is the pause between ads when a shift takes several: the last one
 * was verified and counted, nothing has started yet, and the next one waits for
 * the user to ask for it — a fresh tap, which is also what ad SDKs expect
 * before they play.
 *
 * `captcha` is the human check (Cloudflare Turnstile) before a shift's ad:
 * the server opens no ad without a pass from the last couple of minutes, so
 * the window asks for one first (HumanCheck). `askHuman` shows it and resolves
 * once it is passed (true) or given up (false: closed, or failed with the
 * reason on screen).
 */
export type AdPhase = 'idle' | 'requesting' | 'captcha' | 'playing' | 'verifying' | 'progress' | 'error';

export interface AdError {
  title: string;
  description?: string;
  check?: boolean;
}

interface AdState {
  phase: AdPhase;
  userMinerId: number | null;
  minerName: string;
  remainingSeconds: number;
  totalSeconds: number;
  /** Verified ads counted toward this miner's shift, and how many it takes, as the server last said. */
  adsWatched: number;
  adsRequired: number;
  /** The network that served the session; 'mock' means the server has no real one set up. */
  provider: string;
  /** When the next ad may start (epoch ms), 0 for no wait: Adsgram refuses several ads in a row. */
  nextAdAt: number;
  /** The ad counts only if the player taps inside it (the panel's click rule)… */
  clickRequired: boolean;
  /** …and stays this many seconds with the advertiser after; 0 for no stay. */
  clickDwellSeconds: number;
  /** Why the flow stopped; `check` when it was the human check, not an ad. */
  error: AdError | null;
  /** The human check being asked for: Turnstile's site key, and how long a pass lasts. */
  captcha: { siteKey: string; windowSeconds: number } | null;

  begin(userMinerId: number, minerName: string): void;
  setPhase(phase: AdPhase): void;
  setProgress(remainingSeconds: number, totalSeconds: number): void;
  setCount(adsWatched: number, adsRequired: number): void;
  setProvider(provider: string): void;
  setNextAdAt(at: number): void;
  setClickRequired(clickRequired: boolean, clickDwellSeconds?: number): void;
  fail(error: AdError): void;
  /** Shows the human check; resolves true once passed, false if given up. */
  askHuman(siteKey: string, windowSeconds: number): Promise<boolean>;
  /** The human check's outcome, from HumanCheck. */
  settleHuman(passed: boolean): void;
  reset(): void;
}

const INITIAL = {
  phase: 'idle' as AdPhase,
  userMinerId: null,
  minerName: '',
  remainingSeconds: 0,
  totalSeconds: 0,
  adsWatched: 0,
  adsRequired: 1,
  provider: '',
  nextAdAt: 0,
  clickRequired: false,
  clickDwellSeconds: 0,
  error: null,
  captcha: null,
};

/**
 * Whoever waits on the human check. Kept outside the state: a promise's
 * resolver is not something to render. Anything that ends the check — passing
 * it, closing the window, a failure, a new flow — settles it, so the
 * activation flow never waits on a window that is gone.
 */
let humanWaiter: ((passed: boolean) => void) | null = null;

function settleWaiter(passed: boolean): void {
  const waiter = humanWaiter;
  humanWaiter = null;
  waiter?.(passed);
}

export const useAdStore = create<AdState>((set) => ({
  ...INITIAL,

  begin: (userMinerId, minerName) => {
    settleWaiter(false);
    set({ ...INITIAL, phase: 'requesting', userMinerId, minerName });
  },
  setPhase: (phase) => set({ phase }),
  setProgress: (remainingSeconds, totalSeconds) => set({ remainingSeconds, totalSeconds }),
  setCount: (adsWatched, adsRequired) => set({ adsWatched, adsRequired }),
  setProvider: (provider) => set({ provider }),
  setNextAdAt: (nextAdAt) => set({ nextAdAt }),
  setClickRequired: (clickRequired, clickDwellSeconds = 0) => set({ clickRequired, clickDwellSeconds }),
  fail: (error) => {
    settleWaiter(false);
    set({ phase: 'error', error, captcha: null });
  },
  askHuman: (siteKey, windowSeconds) => {
    settleWaiter(false);
    return new Promise<boolean>((resolve) => {
      humanWaiter = resolve;
      set({ phase: 'captcha', captcha: { siteKey, windowSeconds } });
    });
  },
  settleHuman: (passed) => {
    settleWaiter(passed);
    // Passed: the flow moves on to the ad. Given up: nothing is left to show.
    set(passed ? { phase: 'requesting', captcha: null } : INITIAL);
  },
  reset: () => {
    settleWaiter(false);
    set(INITIAL);
  },
}));

/** The client-side miner state for a miner currently in the ad flow, if any. */
export function adStateFor(userMinerId: number): 'WATCHING_AD' | 'ACTIVATING' | 'ERROR' | null {
  const { phase, userMinerId: active } = useAdStore.getState();
  if (active !== userMinerId) return null;
  if (phase === 'requesting' || phase === 'captcha' || phase === 'playing') return 'WATCHING_AD';
  if (phase === 'verifying') return 'ACTIVATING';
  if (phase === 'error') return 'ERROR';
  return null;
}
